/**
 * Purchase Limits — caps how many of a product fit in one cart.
 *
 * Configured under Theme settings > Purchase Limits and read from the JSON that
 * `snippets/purchase-limit-config.liquid` emits. Renders nothing except a short
 * toast; `layout/theme.liquid` calls in from `addCartItem`, `updateCartItem`
 * and the drawer line template.
 *
 * The limit is per *product*: every variant of a limited product draws from
 * one allowance, so two sizes of the same frame count as two.
 *
 * This is storefront enforcement only. Anything that writes to the cart without
 * going through the theme (the Ajax API called by hand, an app) is not caught —
 * a checkout validation is the only hard guarantee.
 */
(function () {
  "use strict";

  var config = { max: 1, message: "Limit 1 per order", variants: {} };
  var el = document.getElementById("goodr-purchase-limit-config");
  if (el) {
    try {
      config = Object.assign(config, JSON.parse(el.textContent));
    } catch (e) {
      console.error("[PurchaseLimit] Invalid config JSON", e);
    }
  }

  var limitedProducts = {};
  Object.keys(config.variants).forEach(function (variantId) {
    limitedProducts[String(config.variants[variantId])] = true;
  });

  function productIdForVariant(variantId) {
    var productId = config.variants[String(variantId)];
    return productId == null ? null : String(productId);
  }

  function isLimitedVariant(variantId) {
    return productIdForVariant(variantId) !== null;
  }

  function isLimitedLine(item) {
    return !!item && limitedProducts[String(item.product_id)] === true;
  }

  // Quantity of `productId` already in the cart, optionally skipping one line
  // (the line being edited, so its own quantity isn't counted against it).
  function quantityInCart(cart, productId, skipKey) {
    return ((cart && cart.items) || []).reduce(function (sum, item) {
      if (String(item.product_id) !== productId || item.key === skipKey) return sum;
      return sum + item.quantity;
    }, 0);
  }

  // Limited adds run one at a time. Two quick clicks would otherwise both read
  // an empty cart before either add lands, and both would be let through.
  var queue = Promise.resolve();

  /**
   * Resolves to how many of `quantity` may be added for `variantId` (0 when
   * the product is already at its limit). Unlimited variants resolve to
   * `quantity` unchanged without fetching the cart.
   */
  function allowance(variantId, quantity, add) {
    var productId = productIdForVariant(variantId);
    if (productId === null) return add(quantity);

    var run = queue.then(function () {
      return fetch(window.Shopify.routes.root + "cart.js")
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          var remaining = Math.max(config.max - quantityInCart(cart, productId), 0);
          return add(Math.min(quantity, remaining));
        });
    });
    queue = run.catch(function () {});
    return run;
  }

  /**
   * Clamps a drawer quantity change for one line. `cart` is the cart the
   * drawer last rendered, which is what the line index refers to.
   */
  function clampLineQuantity(cart, item, quantity) {
    if (!isLimitedLine(item)) return quantity;
    var others = quantityInCart(cart, String(item.product_id), item.key);
    return Math.min(quantity, Math.max(config.max - others, 0));
  }

  // True when the drawer's "+" for this line should be disabled.
  function atLimit(cart, item) {
    if (!isLimitedLine(item)) return false;
    return quantityInCart(cart, String(item.product_id)) >= config.max;
  }

  var toastTimer = null;
  function notify(message) {
    var toast = document.getElementById("goodr-purchase-limit-toast");
    if (!toast) {
      toast = document.createElement("div");
      toast.id = "goodr-purchase-limit-toast";
      toast.className = "purchase-limit-toast";
      toast.setAttribute("role", "status");
      toast.setAttribute("aria-live", "polite");
      document.body.appendChild(toast);
    }
    toast.textContent = message || config.message;
    toast.classList.add("is-visible");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.classList.remove("is-visible");
    }, 4000);
  }

  window.GoodrPurchaseLimit = {
    config: config,
    isLimitedVariant: isLimitedVariant,
    isLimitedLine: isLimitedLine,
    allowance: allowance,
    clampLineQuantity: clampLineQuantity,
    atLimit: atLimit,
    notify: notify,
  };
})();
